/**
 * SessionGuard — inactivity timeout + active-session keep-alive.
 *
 * Goal: keep ACTIVE users signed in; sign OUT idle users (and anyone whose
 * refresh token is genuinely dead).
 *
 *   - Active users: real input (mouse/keyboard/scroll/touch) resets the idle
 *     clock, and the access token is proactively refreshed just before it
 *     expires — so they are never kicked while working.
 *   - Idle users: after IDLE_MS with no input, a 60-second warning modal shows;
 *     if still idle at the limit, we invalidate the refresh token server-side
 *     and redirect to /login ("signed out due to inactivity").
 *   - Cross-tab: activity in ANY tab keeps ALL tabs alive (shared via
 *     localStorage `lastActivityAt`); a logout in one tab propagates to the rest.
 *
 * Mount <SessionGuard/> once at app root. It no-ops until a session exists, so
 * the /login screen and logged-out state are unaffected.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import config from '../../../config';
import { refreshAccessToken } from './tokenRefresh';
import { handleSessionExpired } from './sessionExpired';
import { updateSocketToken } from './useV3Socket';
import IdleWarningModal from '../Layout/IdleWarningModal';

const IDLE_MS = 15 * 60 * 1000;   // sign out after 15 min of no input
const WARN_MS = 60 * 1000;        // show the warning modal 60s before that
const TICK_MS = 1000;             // evaluate idle + countdown every second
const REFRESH_LEAD_MS = 2 * 60 * 1000; // refresh the access token when <2min to expiry
const ACTIVITY_KEY = 'lastActivityAt';
const STORAGE_WRITE_THROTTLE_MS = 3000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

function isLoggedIn() {
    try {
        return localStorage.getItem('login') === 'true' && !!localStorage.getItem('sessiontoken');
    } catch (_) {
        return false;
    }
}

function onLoginRoute() {
    const p = (typeof window !== 'undefined' && window.location.pathname) || '';
    return p.endsWith('/login');
}

/** Decode a JWT's `exp` (ms epoch). Returns null when unparseable. */
function tokenExpiryMs(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch (_) {
        return null;
    }
}

/** Best-effort server-side logout so the refresh token is invalidated, not just dropped locally. */
function serverLogoutBestEffort() {
    try {
        const token = localStorage.getItem('sessiontoken');
        if (!token) return;
        fetch(`${config.hostname}/auth/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: token },
            keepalive: true,
        }).catch(() => { /* ignore — local teardown happens regardless */ });
    } catch (_) { /* noop */ }
}

export default function SessionGuard() {
    const [warnOpen, setWarnOpen] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(60);

    const lastActivityRef = useRef(Date.now());
    const lastStorageWriteRef = useRef(0);
    const refreshingRef = useRef(false);

    // Record activity (this tab + shared across tabs, throttled).
    const markActivity = useCallback(() => {
        const now = Date.now();
        lastActivityRef.current = now;
        if (now - lastStorageWriteRef.current > STORAGE_WRITE_THROTTLE_MS) {
            lastStorageWriteRef.current = now;
            try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch (_) { /* noop */ }
        }
    }, []);

    // Newest activity across all tabs.
    const getLastActivity = useCallback(() => {
        let shared = 0;
        try { shared = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10) || 0; } catch (_) { /* noop */ }
        return Math.max(lastActivityRef.current, shared);
    }, []);

    const doIdleLogout = useCallback(() => {
        setWarnOpen(false);
        serverLogoutBestEffort();
        // Silent: the 60s warning modal already told the user why. A blocking
        // alert() after that is redundant and traps them on a frozen page. The
        // generic (unwarned) session-expiry path still alerts.
        handleSessionExpired({ reason: 'idle', silent: true });
    }, []);

    // "Stay signed in" — treat as fresh activity + renew the token immediately.
    const staySignedIn = useCallback(() => {
        markActivity();
        setWarnOpen(false);
        Promise.resolve(refreshAccessToken()).then((t) => { if (t) updateSocketToken(t); });
    }, [markActivity]);

    useEffect(() => {
        // Seed activity so a fresh mount doesn't immediately look idle.
        markActivity();

        const onActivity = () => markActivity();
        ACTIVITY_EVENTS.forEach(ev =>
            window.addEventListener(ev, onActivity, { passive: true }));

        // Cross-tab logout: if the token is cleared elsewhere, follow suit here.
        const onStorage = (e) => {
            if (e.key === 'sessiontoken' && !e.newValue && !onLoginRoute()) {
                handleSessionExpired({ silent: true });
            }
        };
        window.addEventListener('storage', onStorage);

        const interval = setInterval(() => {
            if (!isLoggedIn() || onLoginRoute()) {
                setWarnOpen(false); // idempotent — React skips re-render if unchanged
                return;
            }
            const idle = Date.now() - getLastActivity();

            if (idle >= IDLE_MS) {
                doIdleLogout();
                return;
            }

            if (idle >= IDLE_MS - WARN_MS) {
                // In the warning window — show modal + live countdown.
                setWarnOpen(true);
                setSecondsLeft(Math.max(0, Math.ceil((IDLE_MS - idle) / 1000)));
                return;
            }

            setWarnOpen(false); // not in the warning window — ensure modal is closed

            // Active + token close to expiry → renew so REST + socket stay valid.
            const token = localStorage.getItem('sessiontoken');
            const exp = token ? tokenExpiryMs(token) : null;
            if (exp && exp - Date.now() < REFRESH_LEAD_MS && !refreshingRef.current) {
                refreshingRef.current = true;
                Promise.resolve(refreshAccessToken())
                    .then((t) => { if (t) updateSocketToken(t); })
                    .finally(() => { refreshingRef.current = false; });
            }
        }, TICK_MS);

        return () => {
            ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
            window.removeEventListener('storage', onStorage);
            clearInterval(interval);
        };
        // Single setup on mount; the callbacks used inside are stable (useCallback []),
        // and state is written via setters (never read stale).
    }, [markActivity, getLastActivity, doIdleLogout]);

    if (!warnOpen) return null;
    return (
        <IdleWarningModal
            secondsLeft={secondsLeft}
            onStay={staySignedIn}
            onLogout={doIdleLogout}
        />
    );
}
